// Gate decision surface. The page IS a structured rendering of the host
// artifact's decision-relevant content - the human decides from this page in
// five minutes without reading raw markdown. Open questions from the intake
// get inline answer boxes; answers are woven into the recorded rationale.

import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type {
  ArtifactResponse,
  GateMenuOption,
  GateNumber,
  ParsedSection,
} from "@ideacrunch/shared";
import { useArtifact } from "../api";
import { useRunContext } from "./RunView";
import { AttachFiles, uploadBatch } from "../components/AttachFiles";
import {
  BriefingDownload,
  LongText,
  MarkdownView,
  sectionText,
} from "../components/bits";
import { useVocab } from "../vocab";

const CANONICAL_MENUS: Record<GateNumber, GateMenuOption[]> = {
  0: [
    { n: 1, label: "Approve this framing", description: "Research starts on exactly this problem statement." },
    { n: 2, label: "Correct it", description: "Say what's wrong; intake reframes before any research burns effort." },
    { n: 3, label: "Add constraints or context", description: "The framing holds, but bind extra context to the run." },
    { n: 4, label: "Kill it now", description: "The run is still archived - kill reasons are valuable residue." },
  ],
  1: [
    { n: 1, label: "Blueprint it", description: "Build the implementation plan for the recommended direction." },
    { n: 2, label: "Pivot", description: "Re-plan with a named change." },
    { n: 3, label: "Dig deeper on one track", description: "Send a named research lane back for another round." },
    { n: 4, label: "Pause", description: "Park it with a re-entry trigger." },
    { n: 5, label: "Kill", description: "What dies and what residue survives gets recorded." },
  ],
  2: [
    { n: 1, label: "Approve the plan", description: "Execute as written." },
    { n: 2, label: "Send it back once", description: "One bounce to the Blueprint with directives; the loop is bounded." },
    { n: 3, label: "Downgrade to experiments only", description: "Run the cheap validation tests first, then re-decide." },
    { n: 4, label: "Kill", description: "The evidence and residue survive to the library." },
  ],
};

/** Section headings that matter most per gate, shown open; others collapsed. */
const PRIMARY_SECTIONS: Record<GateNumber, RegExp> = {
  0: /problem statement|constraints|success definition|know or believe|claims register|candidates|founder profile/i,
  1: /^in plain terms|^why|kill criteria|evidence for|evidence against|unknowns/i,
  2: /plan verdict|directives|gaps|steelman/i,
};

function splitQuestions(text: string): string[] {
  return text
    .split(/(?:^|\n)(?=\d+\.\s)/)
    .filter((p) => /^\d+\.\s/.test(p))
    .map((p) => p.replace(/^\d+\.\s*/, "").replace(/\s+/g, " ").trim());
}

export function GateView() {
  const run = useRunContext();
  const params = useParams();
  const gate = Number(params["gate"] ?? "0") as GateNumber;
  const { gateTitle } = useVocab();

  const info = run.gates.find((g) => g.gate === gate);
  const hostPath = info?.artifactPath ?? "";
  const { data: host } = useArtifact(run.id, hostPath);

  const [choice, setChoice] = useState<number | null>(null);
  const [otherText, setOtherText] = useState("");
  const [rationale, setRationale] = useState("");
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [files, setFiles] = useState<File[]>([]);
  const [submitState, setSubmitState] = useState<
    "idle" | "submitting" | "error" | "done"
  >("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  // Context sections: everything except the gate block itself, in artifact
  // order. The "Open questions" section is rendered specially with inline
  // answer boxes when the gate is pending.
  const { contextSections, questionSection, questions, gateSection } =
    useMemo(() => {
      const all = (host?.parsed.sections ?? []).filter(
        (s) => s.depth === 2 && s.revisionRound === null,
      );
      const gSection =
        all.find((s) => /^Gate\s+\d/i.test(s.heading)) ?? null;
      const sections = all.filter((s) => s !== gSection);
      const qSection =
        sections.find((s) => /open questions/i.test(s.heading)) ?? null;
      return {
        contextSections: sections.filter((s) => s !== qSection),
        questionSection: qSection,
        questions:
          qSection && host
            ? splitQuestions(sectionText(host.raw, qSection))
            : [],
        gateSection: gSection,
      };
    }, [host]);

  if (!info) {
    return (
      <div className="error-box">
        Gate {gate} has no artifact yet - the machine has not reached it.{" "}
        <Link to={`/runs/${run.id}`}>Back to the run</Link>
      </div>
    );
  }

  const menu: GateMenuOption[] =
    info.menu.length > 0 ? info.menu : CANONICAL_MENUS[gate];
  const primary = PRIMARY_SECTIONS[gate];

  const submit = async () => {
    setSubmitState("submitting");
    const decisionText =
      choice === -1
        ? otherText
        : (menu.find((m) => m.n === choice)?.label ?? "");
    const answerLines = questions
      .map((_, i) => answers[i]?.trim())
      .map((a, i) => (a ? `(${i + 1}) ${a}` : null))
      .filter((x): x is string => x !== null);
    const fullRationale =
      rationale.trim() +
      (answerLines.length > 0
        ? ` Answers to the open questions: ${answerLines.join(" ")}`
        : "");
    try {
      const uploadBatchId = files.length > 0 ? await uploadBatch(files) : null;
      const res = await fetch(
        `/api/runs/${encodeURIComponent(run.id)}/gates/${gate}/decision`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            choice: choice === -1 ? null : choice,
            decisionText,
            rationale: fullRationale,
            artifactSha: host?.meta.contentSha ?? null,
            uploadBatchId,
          }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(body.error ?? `${res.status} ${res.statusText}`);
      }
      setSubmitState("done");
      setSubmitMessage(
        "Decision recorded in the artifact. The machine resumes from here.",
      );
    } catch (err) {
      setSubmitState("error");
      setSubmitMessage(
        err instanceof Error ? err.message : "submission failed",
      );
    }
  };

  return (
    <div>
      <div className={`gate-banner ${info.pending ? "" : "decided"}`}>
        <h1>{gateTitle(gate)}</h1>
        {info.pending ? (
          <div className="verdict-line">
            The machine is stopped for you - that's the design. Review the
            context below, answer what's asked, and make the call.{" "}
            <a href="#decision-form">Jump to the decision.</a>
          </div>
        ) : (
          <div className="verdict-line">
            <strong>Decision:</strong> {info.decisionRaw}
            {info.decidedBy && (
              <>
                {" "}
                · <strong>by:</strong> {info.decidedBy}
              </>
            )}
            {info.rationale && (
              <div style={{ marginTop: 4, color: "var(--ink-2)" }}>
                <LongText text={info.rationale} />
              </div>
            )}
          </div>
        )}
        {host?.parsed.meta["Plan verdict"] && (
          <div className="verdict-line" style={{ marginTop: 6 }}>
            <strong>Skeptic's verdict:</strong>{" "}
            {host.parsed.meta["Plan verdict"]}
          </div>
        )}
      </div>

      {/* Decision-relevant context, structured - never a raw dump. */}
      {host &&
        contextSections.map((s) => (
          <ContextSection
            key={s.heading}
            host={host}
            section={s}
            defaultOpen={primary.test(s.heading)}
          />
        ))}

      {/* The gate section often carries the writer's own guidance for this
          decision (e.g. the Skeptic's recommendation) - show it while the
          decision is open. Placeholder Decision/By lines render harmlessly. */}
      {info.pending && host && gateSection && (
        <div className="panel" style={{ borderColor: "var(--accent)" }}>
          <h2>Guidance for this decision</h2>
          <MarkdownView text={sectionText(host.raw, gateSection)} collapsible />
        </div>
      )}

      {info.pending && questionSection && questions.length > 0 && (
        <div className="panel" style={{ borderColor: "var(--warn)" }}>
          <h2>{questionSection.heading}</h2>
          <p style={{ color: "var(--ink-2)", fontSize: 13.5, marginTop: 0 }}>
            Your answers get recorded with the decision and become binding
            context for the run.
          </p>
          {questions.map((q, i) => (
            <div key={i} style={{ marginBottom: 14 }}>
              <div style={{ marginBottom: 6 }}>
                <span className="chip accent">Q{i + 1}</span>{" "}
                <span>{q}</span>
              </div>
              <textarea
                className="gate-form"
                style={{
                  width: "100%",
                  background: "var(--surface)",
                  color: "var(--ink)",
                  border: "1px solid var(--hairline)",
                  borderRadius: 8,
                  padding: "8px 10px",
                  font: "inherit",
                }}
                rows={2}
                placeholder="Your answer (optional)"
                value={answers[i] ?? ""}
                onChange={(e) =>
                  setAnswers((prev) => ({ ...prev, [i]: e.target.value }))
                }
              />
            </div>
          ))}
          <div style={{ marginTop: 4 }}>
            <AttachFiles files={files} onChange={setFiles} />
            <p className="parse-warnings" style={{ marginTop: 6 }}>
              Files sent with your answers are committed into the run's
              inputs and named in the recorded rationale, so the machine
              reads them when it resumes.
            </p>
          </div>
        </div>
      )}

      {info.pending && <BriefingDownload run={run} />}

      {info.pending && (
        <div className="panel gate-form" id="decision-form">
          <h2>Your decision</h2>
          <div className="gate-options">
            {menu.map((opt) => (
              <label
                key={opt.n}
                className={`gate-option ${choice === opt.n ? "selected" : ""}`}
              >
                <input
                  type="radio"
                  name="gate-choice"
                  checked={choice === opt.n}
                  onChange={() => setChoice(opt.n)}
                />
                <span>
                  <div className="opt-label">{opt.label}</div>
                  {opt.description && (
                    <div className="opt-desc">{opt.description}</div>
                  )}
                </span>
              </label>
            ))}
            <label className={`gate-option ${choice === -1 ? "selected" : ""}`}>
              <input
                type="radio"
                name="gate-choice"
                checked={choice === -1}
                onChange={() => setChoice(-1)}
              />
              <span style={{ flex: 1 }}>
                <div className="opt-label">Other</div>
                <input
                  type="text"
                  placeholder='recorded verbatim, e.g. "approved, run at quick depth"'
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  onFocus={() => setChoice(-1)}
                />
              </span>
            </label>
          </div>
          <div style={{ margin: "10px 0" }}>
            <textarea
              rows={2}
              placeholder="Rationale (required - one line on why, recorded in the artifact)"
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
            />
          </div>
          {(!questionSection || questions.length === 0) && (
            <div style={{ margin: "0 0 10px" }}>
              <AttachFiles files={files} onChange={setFiles} />
            </div>
          )}
          <button
            className="btn"
            disabled={
              submitState === "submitting" ||
              submitState === "done" ||
              choice === null ||
              (choice === -1 && otherText.trim() === "") ||
              rationale.trim() === ""
            }
            onClick={() => void submit()}
          >
            {submitState === "submitting"
              ? "Recording..."
              : submitState === "done"
                ? "Recorded"
                : "Record decision"}
          </button>
          {submitState === "error" && (
            <span style={{ marginLeft: 10, color: "var(--critical)" }}>
              {submitMessage}
            </span>
          )}
          {submitState === "done" && (
            <span style={{ marginLeft: 10, color: "var(--good-text)" }}>
              {submitMessage}
            </span>
          )}
        </div>
      )}

      {host && (
        <details className="section-acc">
          <summary>
            Read the full artifact{" "}
            <span className="artifact-caption">({hostPath})</span>
          </summary>
          <div className="panel">
            <MarkdownView text={host.raw} />
          </div>
        </details>
      )}
    </div>
  );
}

function ContextSection({
  host,
  section,
  defaultOpen,
}: {
  host: ArtifactResponse;
  section: ParsedSection;
  defaultOpen: boolean;
}) {
  return (
    <details className="section-acc" open={defaultOpen}>
      <summary>{section.heading}</summary>
      <div style={{ padding: "4px 0 10px" }}>
        <MarkdownView text={sectionText(host.raw, section)} collapsible />
      </div>
    </details>
  );
}
